import React from 'react';
import { KbSection, Callout, StepList, VisibleTo } from '../../components/knowledge/primitives.jsx';

// "Dashboard & analytics" articles. Both are universal — every role lands on
// the dashboard — but the cost material inside the overview is section-gated
// on canSeeCosts, so roles without the cost tier never see it mentioned.

function DashboardOverviewBody({ user }) {
  return (
    <>
      <KbSection title="Your numbers, not everyone's">
        <p>
          The <strong>Dashboard</strong> is the charts-and-numbers view of the cases you're
          allowed to see — the same set as All Cases. If your role is limited to a clinic or a region,
          every count and chart is built from those cases only, automatically. The line under
          the page title always says exactly what you are looking at: the case count, the
          scope, and the time window — for example <em>142 adverse events · Clinic_A · all time</em>.
        </p>
      </KbSection>

      <KbSection title="The headline cards" delay={1}>
        <p>Seven cards summarise the current selection:</p>
        <StepList steps={[
          <><strong>Total</strong> — every case in the current scope and time window.</>,
          <><strong>New</strong>, <strong>Open</strong>, <strong>Pending</strong>, <strong>With Claims</strong>, <strong>Closed</strong> — one card per case status. Each of these is clickable and jumps to All Cases.</>,
          <><strong>This Month</strong> — cases reported in the current calendar month, within the current scope.</>,
        ]} />
        <Callout tone="info">
          The cards recount live as you change the time range or scope — a narrow window can
          legitimately show <strong>This Month</strong> as zero even when cases exist.
        </Callout>
      </KbSection>

      <KbSection title="The charts" delay={2}>
        <p>Below the cards, the breakdowns — all built from the same set of cases:</p>
        <StepList steps={[
          <><strong>Cases over time (monthly)</strong> — three lines over the last 18 months: Treatment, Near miss and Clinic incident.</>,
          <><strong>By status</strong> and <strong>By year</strong> — donuts of the same totals as the headline cards, and of reporting year.</>,
          <><strong>By severity code</strong> — a bar per code, Code 1 to Code 5.</>,
          <><strong>By treatment / department</strong> — ranked bars, busiest first, with a <strong>Show more</strong> button past the top entries.</>,
          <><strong>By country</strong>, <strong>By region</strong> and <strong>By clinic</strong> — geographic breakdowns that adapt to your scope: narrow to a region or clinic and the now-redundant ones disappear.</>,
        ]} />
        <p>
          Chart colours are not decorative — status and severity segments use the same colours
          as the badges on the case list, so a teal slice here and a teal badge there mean the
          same thing.
        </p>
      </KbSection>

      <VisibleTo user={user} when={(u) => !!u?.canSeeCosts}>
        <KbSection title="Costs on the dashboard" delay={3}>
          <p>
            The <strong>Costs · by category</strong> panel rolls up every cost line on the visible
            cases. One tile per currency in use — <strong>Total cost (EUR)</strong>,{' '}
            <strong>Total cost (GBP)</strong> — because totals are <em>never</em> converted or
            combined across currencies. When more than one currency is present, toggle buttons
            in the panel head (e.g. <strong>€ EUR</strong>) switch the category chart between them.
          </p>
          <p>
            The chart shows one bar per cost category, coloured to match the category pills on
            the case page. Categories come from your admin's configuration — <em>Refund</em>,{' '}
            <em>Legal fees</em> and <em>Settlement sum</em> are typical examples. Like everything
            else on the page, the totals follow the timeline: narrow the range and they update.
          </p>
        </KbSection>
      </VisibleTo>
    </>
  );
}

function DashboardTimelineBody() {
  return (
    <>
      <KbSection title="Picking a time window">
        <p>
          The <strong>Timeline</strong> panel sits above the KPI row. Its header restates the
          current selection — scope, range and case count — and offers four presets:{' '}
          <strong>30d</strong>, <strong>60d</strong>, <strong>All</strong> and <strong>Custom</strong>.
        </p>
        <StepList steps={[
          <>Press <strong>Custom</strong> — a <strong>From</strong> / <strong>To</strong> date row opens under the presets.</>,
          <>Pick both dates and press <strong>Apply</strong>. The window commits and the row collapses.</>,
          <><strong>Clear</strong> wipes the custom window and returns the range to <strong>All</strong>.</>,
        ]} />
      </KbSection>

      <KbSection title="The timeline brush" delay={1}>
        <p>
          The strip of bars shows your whole history week by week — the taller the bar, the
          busier the week. Drag the two round handles at its base to zoom onto any period: the
          header label updates (e.g. <em>2 Mar 26 – 28 Jun 26</em>) and every card and chart on
          the page follows. Drag the handles back to the full width and the range resets
          to <strong>All</strong>.
        </p>
        <Callout tone="info">
          The scope and range you set are part of the page URL. <strong>Copy link</strong> in the
          page head copies the current view exactly, so a colleague opening your link sees the
          same period and scope you were looking at.
        </Callout>
      </KbSection>

      <KbSection title="Geographic scope" delay={2}>
        <p>
          The <strong>Scope</strong> panel at the top switches whose cases the whole page counts:{' '}
          <strong>All</strong>, <strong>By region</strong> or <strong>By clinic</strong>. The last two
          open a searchable <strong>Region</strong> or <strong>Clinic</strong> picker, and the page
          waits until you choose one before it loads the numbers.
        </p>
        <p>
          Narrowing hides what would be redundant: region scope drops the <strong>By country</strong>{' '}
          and <strong>By region</strong> charts and retitles the clinic chart{' '}
          <strong>Clinics in region</strong>; clinic scope hides the geographic charts entirely.
          Changing scope also resets the time range to <strong>All</strong>, so set scope first,
          then narrow the period.
        </p>
      </KbSection>

      <KbSection title="Staff breakdowns for a single clinic" delay={3}>
        <p>
          Scope to one clinic and a <strong>Reported staff</strong> panel appears under the
          headline cards. Four cards lead it: <strong>1st offence</strong>, <strong>2nd offence</strong>,{' '}
          <strong>3rd+ offence</strong> and <strong>Staff named</strong>. The tier is simply how
          many reports name that staff member within the current time window — one report is a
          1st offence, two a 2nd, three or more a 3rd+.
        </p>
        <p>
          Below the cards, every named staff member is listed most-reported first with their
          tier and the date of their latest report; when the list is short a bar chart renders
          above it. Cases submitted without a staff member are not silently dropped — a footnote
          counts them so you know the breakdown's coverage.
        </p>
        <Callout tone="warn">
          Offence tiers only count reports in the <em>current time window</em>. A narrow brush can
          make a repeat pattern look like a first occurrence — widen the range to{' '}
          <strong>All</strong> before drawing conclusions about an individual.
        </Callout>
      </KbSection>
    </>
  );
}

export const DASHBOARD_ARTICLES = [
  {
    id: 'dashboard-overview',
    title: 'Reading the dashboard',
    category: 'dashboard',
    summary: 'What the KPI cards and charts show, and whose cases they count.',
    keywords: ['dashboard', 'kpi', 'charts', 'analytics', 'status', 'severity', 'trend', 'overview'],
    visibleTo: () => true,
    Body: DashboardOverviewBody,
  },
  {
    id: 'dashboard-timeline',
    title: 'Time ranges, scoping and staff breakdowns',
    category: 'dashboard',
    summary: 'Narrow the dashboard to a period, a region or a clinic — and read the per-staff view.',
    keywords: ['timeline', 'brush', 'date range', 'scope', 'region', 'clinic', 'staff', 'offence', 'filter'],
    visibleTo: () => true,
    Body: DashboardTimelineBody,
  },
];
